import { PointColorMode } from '../../../../../cross-team-shared/state/layer-settings-state-3d';
import {
  getColorForSubpointMeasurementMode,
  subpointMeasurementModeUniforms,
  SubpointMeasurementModeUniforms,
} from '../subpoint-measurement-mode';
import {
  getColorForHeightMode,
  heightModeUniforms,
  HeightModeUniforms,
} from './modes/height';
import {
  getColorForInsideCuboidMode,
  insideCuboidModeUniforms,
  InsideCuboidModeUniforms,
} from './modes/inside-cuboid';
import {
  getColorForIntensityMode,
  intensityModeUniforms,
  IntensityModeUniforms,
} from './modes/intensity';
import {
  regionOfInterest,
  regionOfInterestUniforms,
  RegionOfInterestUniforms,
} from './modes/region-of-interest';
import {
  getColorForSensorMode,
  sensorModeUniforms,
  SensorModeUniforms,
} from './modes/sensor';
import {
  viewBoxHighlight,
  viewBoxHighlightUniforms,
  ViewBoxHighlightUniforms,
} from './modes/viewbox-highlight';

// Adapted from https://gamedev.stackexchange.com/a/59808
const hsvToRgba = `
  vec4 hsvToRgba(vec3 c) {
    vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
    vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
    return vec4(c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y), 1.0);
  }
  `;

// Based on https://learnopengl.com/Advanced-OpenGL/Blending
const getGrayishBlendColor = `
  vec4 getGrayishBlendColor(vec4 originalColor) {
    vec4 black = vec4(0.0, 0.0, 0.0, 0.9);
    return black * black.a + originalColor * (originalColor.a - black.a);
  }
`;

const colorUniforms = `
  ${regionOfInterestUniforms}
  ${viewBoxHighlightUniforms}
  ${subpointMeasurementModeUniforms}
  ${intensityModeUniforms}
  ${insideCuboidModeUniforms}
  ${heightModeUniforms}
  ${sensorModeUniforms}

  struct ColorUniforms {
    int mode;
    RegionOfInterestUniforms regionOfInterest;
    ViewBoxHighlightUniforms viewBoxHighlight;
    SubpointMeasurementModeUniforms subpointMeasurement;
    IntensityModeUniforms intensity;
    InsideCuboidModeUniforms insideCuboid;
    HeightModeUniforms height;
    SensorModeUniforms sensor;
  };

  uniform ColorUniforms color;
`;

const colorFunctions = `
  ${regionOfInterest}
  ${viewBoxHighlight}
  ${getColorForSubpointMeasurementMode}
  ${getColorForIntensityMode}
  ${getColorForInsideCuboidMode}
  ${getColorForHeightMode}
  ${getColorForSensorMode}
`;

export const getColor = `
  #define CLOUD_INSTANCE_ID uint(0)
  #define GROUND_INSTANCE_ID uint(65535)

  varying vec4 vColor;
  attribute uint instanceId;
  uniform bool useSubPointMeasurementMode;

  struct HSVPalette {
    vec3 withoutIntensity;
    vec3 min;
    vec3 max;
  };

  struct Range {
      float min;
      float max;
  };

  const int intensityMode = 0;
  const int sensorMode = 1;
  const int insideCuboidMode = 2;
  const int heightMode = 3;

  ${colorUniforms}
  ${hsvToRgba}
  ${getGrayishBlendColor}
  ${colorFunctions}

  vec4 getColorForMode() {
    if(useSubPointMeasurementMode) {
      return getColorForSubpointMeasurementMode();
    }

    switch (color.mode) {
      case intensityMode:
        return getColorForIntensityMode();
      case sensorMode:
        return getColorForSensorMode();
      case insideCuboidMode:
        return getColorForInsideCuboidMode(vec4(0.478, 0.478, 0.478, 1.0));
      case heightMode:
        return getColorForHeightMode();
    }
  }

  vec4 getColor() {
    vec4 colorToApply = getColorForMode();

    if (isViewBoxHighlightEnabled() && !isPointVisibleInSplitView()) {
      colorToApply = getGrayishBlendColor(colorToApply);
    } else if (isRegionOfInterestEnabled() && !isPointWithinRegionOfInterest()) {
      colorToApply = getGrayishBlendColor(colorToApply);
    }
    colorToApply = getColorForInsideCuboidMode(colorToApply);

    return colorToApply;
  }
`;

export interface CloudColorUniforms {
  mode: PointColorMode;
  viewBoxHighlight: ViewBoxHighlightUniforms;
  regionOfInterest: RegionOfInterestUniforms;
  subpointMeasurement: SubpointMeasurementModeUniforms;
  intensity: IntensityModeUniforms;
  height: HeightModeUniforms;
  sensor: SensorModeUniforms;
  insideCuboid: InsideCuboidModeUniforms;
}
